import { Component, OnInit, inject, input, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { catchError, of } from 'rxjs';
import { PropertyAdminService } from '../../../core/services/property-admin.service';
import { PropertyResponse } from '../../../core/models';
import { FcfaPipe } from '../../../shared/pipes/fcfa.pipe';
import { PROPERTY_STATUSES, PropertyStatus, propertyStatusLabel, propertyTypeLabel } from '../property-labels';

interface KanbanColumn {
  status: PropertyStatus;
  items: PropertyResponse[];
  total: number;
  page: number;
  loading: boolean;
}

const COLUMN_PAGE_SIZE = 20;

/**
 * Vue kanban des biens : une colonne par étape de l'annonce (PropertyStatus).
 * Chaque colonne est chargée indépendamment via GET /admin/properties?status=…
 * et se pagine avec « Charger plus » (ajout à la suite des cartes déjà chargées).
 */
@Component({
  selector: 'app-property-kanban',
  standalone: true,
  imports: [CommonModule, RouterLink, ButtonModule, ProgressSpinnerModule, FcfaPipe],
  templateUrl: './property-kanban.html',
  styleUrl: './property-kanban.scss'
})
export class PropertyKanbanComponent implements OnInit {
  private svc = inject(PropertyAdminService);

  // Filtres locaux, appliqués aux cartes déjà chargées (pas de recherche côté backend).
  search = input('');
  type   = input('');

  columns = signal<KanbanColumn[]>(
    PROPERTY_STATUSES.map(status => ({ status, items: [], total: 0, page: 0, loading: true }))
  );

  filteredColumns = computed(() => {
    const term = this.search().trim().toLowerCase();
    const type = this.type();
    return this.columns().map(col => ({
      ...col,
      visible: col.items.filter(p =>
        (!term || p.title?.toLowerCase().includes(term) || p.city?.toLowerCase().includes(term) || p.commune?.toLowerCase().includes(term))
        && (!type || p.type === type))
    }));
  });

  ngOnInit() {
    PROPERTY_STATUSES.forEach(status => this.loadColumn(status, 0));
  }

  loadMore(status: PropertyStatus) {
    const col = this.columns().find(c => c.status === status);
    if (col && !col.loading) this.loadColumn(status, col.page + 1);
  }

  private loadColumn(status: PropertyStatus, page: number) {
    this.patch(status, { loading: true });
    this.svc.getAllProperties(page, COLUMN_PAGE_SIZE, status)
      .pipe(catchError(() => of(null)))
      .subscribe(res => {
        if (!res) { this.patch(status, { loading: false }); return; }
        const current = this.columns().find(c => c.status === status)!;
        this.patch(status, {
          items: page === 0 ? res.content : [...current.items, ...res.content],
          total: res.totalElements,
          page,
          loading: false
        });
      });
  }

  private patch(status: PropertyStatus, changes: Partial<KanbanColumn>) {
    this.columns.update(cols => cols.map(c => c.status === status ? { ...c, ...changes } : c));
  }

  statusLabel = propertyStatusLabel;
  typeLabel   = propertyTypeLabel;

  formatDate(d: string) { return d ? new Date(d).toLocaleDateString('fr-FR') : '—'; }
}
